/**
 * update-component command — update individual Admin components from the latest CLI templates
 */

import { execFileSync } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'
import * as clack from '@clack/prompts'
import type { UpdateCommandOptions } from '@core-engine/commands/runtime.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { resolveCliAssetPath } from '@core-engine/utils/cli-root.js'
import type { BetterstartIntegrationId } from '@integration-engine/types.js'
import { detectProjectName } from '@next-adapter/config/detect.js'
import {
  applyAdminNamespaceToContent,
  applyAdminNamespaceToPath,
  resolveAdminNamespace
} from '@next-adapter/config/namespace.js'
import { resolveConfig } from '@next-adapter/config.js'
import { adminDataTemplate } from '@next-adapter/init/templates/app/admin.js'
import {
  renderCoreEmailProvider,
  renderCoreEmailProviderIndex,
  renderCoreStorageProvider,
  renderCoreStorageProviderIndex,
  syncInstalledIntegrationManifests
} from '@next-adapter/integration-runtime.js'
import { readIntegrationTemplate } from '@next-adapter/integration-template-reader.js'
import { syncInstalledPluginManifests } from '@next-adapter/plugin-runtime.js'
import { applyNextCacheApiForVersion, readTemplate } from '@next-adapter/template-reader.js'
import type { BetterstartPluginId } from '@plugin-engine/types.js'
import fsExtra from 'fs-extra'

interface TemplateEntry {
  relPath: string | ((context: TemplateContentContext) => string)
  displayPath?: string
  content: (context: TemplateContentContext) => string
  base?: 'cwd'
  requiredPlugin?: BetterstartPluginId
  requiredIntegration?: BetterstartIntegrationId
  dependencies?: string[]
  preserveExisting?: boolean
}

interface TemplateContentContext {
  config: ResolvedBetterstartConfig
  cwd: string
}

interface StaticAssetComponent {
  name: string
  file: string
}

const STATIC_CUSTOM_DEPENDENCIES: Record<string, string[]> = {
  'content-editor': [
    'admin-globals',
    'content-editor/main-toolbar-content',
    'content-editor-utils',
    'content-editor-rich-extensions',
    'content-editor-editor-view-utils',
    'markdown-utils',
    'media-field',
    'use-content-editor',
    'use-content-editor-table-add-controls',
    'use-content-editor-source-mode',
    'use-content-editor-mobile-toolbar',
    'use-content-editor-slash-menu',
    'use-admin-theme',
    'use-mobile',
    'tiptap',
    'tiptap-utils'
  ],
  'content-editor/tiptap-ui/blockquote-button/blockquote-button': ['use-blockquote'],
  'content-editor/tiptap-ui/code-block-button/code-block-button': ['use-code-block'],
  'content-editor/tiptap-ui/color-highlight-button/color-highlight-button': ['use-color-highlight'],
  'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover': [
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-button',
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-content',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors',
    'content-editor/tiptap-ui/color-highlight-popover/source-color-highlight-popover',
    'use-color-highlight'
  ],
  'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-content': [
    'content-editor/tiptap-ui/color-highlight-popover/highlight-color-button',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors',
    'content-editor/use-menu-navigation',
    'use-color-highlight'
  ],
  'content-editor/tiptap-ui/color-highlight-popover/highlight-color-button': [
    'content-editor/tiptap-ui/color-highlight-button/color-highlight-button',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors'
  ],
  'content-editor/tiptap-ui/color-highlight-popover/highlight-colors': ['use-color-highlight'],
  'content-editor/tiptap-ui/color-highlight-popover/source-color-highlight-popover': [
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-button',
    'content-editor/tiptap-ui/color-highlight-popover/color-highlight-popover-content',
    'content-editor/tiptap-ui/color-highlight-popover/highlight-colors'
  ],
  'content-editor/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu': [
    'content-editor/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu-item',
    'use-heading',
    'use-heading-dropdown-menu'
  ],
  'content-editor/tiptap-ui/heading-dropdown-menu/heading-dropdown-menu-item': ['use-heading'],
  'content-editor/tiptap-ui/link-popover/link-content': [
    'content-editor/tiptap-ui/link-popover/link-main',
    'use-link-popover'
  ],
  'content-editor/tiptap-ui/link-popover/link-control-popover': [
    'content-editor/tiptap-ui/link-popover/link-button',
    'content-editor/tiptap-ui/link-popover/link-main'
  ],
  'content-editor/tiptap-ui/link-popover/link-popover': [
    'content-editor/tiptap-ui/link-popover/link-content',
    'content-editor/tiptap-ui/link-popover/link-control-popover',
    'use-link-popover'
  ],
  'content-editor/tiptap-ui/list-dropdown-menu/list-dropdown-menu': [
    'content-editor/tiptap-ui/list-dropdown-menu/list-dropdown-menu-item',
    'use-list',
    'use-list-dropdown-menu'
  ],
  'content-editor/tiptap-ui/list-dropdown-menu/list-dropdown-menu-item': ['use-list'],
  'content-editor/tiptap-ui/mark-button/mark-button': ['use-mark'],
  'content-editor/tiptap-ui/undo-redo-button/undo-redo-button': ['use-undo-redo'],
  'content-editor/main-toolbar-content': [
    'content-editor',
    'content-editor/math-popover',
    'use-content-editor-media-insertion'
  ],
  'content-editor/math-popover': [
    'content-editor/math-bubble-menu',
    'content-editor/math-button',
    'content-editor/math-editor-controls',
    'content-editor/math-popover-button',
    'content-editor/use-tiptap-editor',
    'markdown-utils',
    'tiptap-utils'
  ],
  'content-editor/math-bubble-menu': [
    'content-editor/math-editor-controls',
    'content-editor/use-tiptap-editor',
    'markdown-utils',
    'tiptap-utils'
  ],
  'content-editor/math-popover-button': [
    'content-editor/math-button',
    'content-editor/math-editor-controls',
    'content-editor/use-tiptap-editor',
    'tiptap-utils'
  ],
  'content-editor/media-gallery-block': ['media-field'],
  'content-editor/mobile-toolbar-content': [
    'content-editor/math-popover',
    'content-editor/use-tiptap-editor',
    'tiptap-utils'
  ],
  'content-editor/selection-bubble-menu': ['content-editor/math-popover'],
  'content-editor/slash-command-menu': ['content-editor-slash-commands-utils', 'tiptap-utils'],
  'content-editor/source-mode': [
    'content-editor/math-popover',
    'use-content-editor-source-mode',
    'tiptap'
  ],
  'content-editor/table-bubble-menu': [
    'content-editor-table-bubble-utils',
    'content-editor/remove-table-part-icon',
    'tiptap-utils'
  ],
  'content-editor/table-button': [
    'content-editor-table-input-utils',
    'tiptap-utils',
    'use-table-button'
  ],
  'date-range-picker': ['date-utils'],
  'gallery-field': [
    'card',
    'carousel',
    'media-field',
    'media-field-empty-state',
    'media-gallery-dialog',
    'skeleton',
    'sortable-gallery-item',
    'use-media'
  ],
  'icon-picker': ['icons-column-skeleton', 'icons-data'],
  'media-field': [
    'media-field-empty-state',
    'media-gallery-dialog',
    'media-preview',
    'media-fallback-utils',
    'use-copy-to-clipboard',
    'use-media'
  ],
  'media-field-empty-state': ['button', 'card', 'cn', 'media-gallery-dialog'],
  'nested-object-list-field': ['form-list-field-utils'],
  'progressive-blur': ['use-page-boundary-blur-visibility'],
  'upload-dropzone': ['upload-utils']
}

const TIPTAP_TEMPLATE_DEPENDENCIES = [
  'use-blockquote',
  'use-code-block',
  'use-color-highlight',
  'content-editor/use-is-breakpoint',
  'content-editor/use-menu-navigation',
  'content-editor/use-tiptap-editor',
  'use-content-editor-table-add-controls',
  'use-table-button',
  'content-editor-editor-view-utils',
  'use-heading',
  'use-heading-dropdown-menu',
  'use-link-popover',
  'use-list',
  'use-list-dropdown-menu',
  'use-mark',
  'use-undo-redo',
  'content-editor-node-attrs-utils',
  'content-editor-slash-commands-utils',
  'content-editor-table-add-controls-utils',
  'content-editor-table-bubble-utils',
  'content-editor-table-input-utils',
  'content-editor-task-item-utils',
  'content-editor-source-media-utils',
  'content-editor/main-toolbar-content',
  'content-editor/source-mode',
  'tiptap-utils',
  'content-editor/media-gallery-block',
  'media-field'
]

const TIPTAP_CONTENT_EDITOR_DIRECTORIES = ['tiptap-extension', 'tiptap-node', 'tiptap-ui']

const CODEMIRROR_PACKAGE_DEPS = [
  '@codemirror/commands',
  '@codemirror/lang-javascript',
  '@codemirror/lang-json',
  '@codemirror/lang-markdown',
  '@codemirror/state',
  '@codemirror/view',
  '@uiw/codemirror-theme-github',
  '@uiw/react-codemirror'
]

const TIPTAP_PACKAGE_DEPS = [
  '@tiptap/core',
  '@tiptap/extension-underline',
  '@tiptap/extension-highlight',
  '@tiptap/extension-horizontal-rule',
  '@tiptap/extension-image',
  '@tiptap/extension-list',
  '@tiptap/extension-placeholder',
  '@tiptap/extension-table',
  '@tiptap/extension-table-cell',
  '@tiptap/extension-table-header',
  '@tiptap/extension-table-row',
  '@tiptap/extension-typography',
  '@tiptap/extensions',
  '@tiptap/markdown',
  '@tiptap/pm',
  '@tiptap/react',
  '@tiptap/starter-kit'
]

const KATEX_PACKAGE_DEPS = ['katex']

const TAILWIND_TYPOGRAPHY_PACKAGE_DEPS = ['@tailwindcss/typography']

const COMPONENT_PACKAGE_DEPENDENCIES: Record<string, string[]> = {
  'admin-globals': [...TAILWIND_TYPOGRAPHY_PACKAGE_DEPS, ...KATEX_PACKAGE_DEPS],
  'content-editor': [
    ...TIPTAP_PACKAGE_DEPS,
    ...CODEMIRROR_PACKAGE_DEPS,
    ...TAILWIND_TYPOGRAPHY_PACKAGE_DEPS,
    ...KATEX_PACKAGE_DEPS
  ],
  'content-editor-rich-extensions': KATEX_PACKAGE_DEPS,
  'use-content-editor': [
    ...TIPTAP_PACKAGE_DEPS,
    ...TAILWIND_TYPOGRAPHY_PACKAGE_DEPS,
    ...KATEX_PACKAGE_DEPS
  ],
  'use-content-editor-media-insertion': ['react-hotkeys-hook'],
  'use-color-highlight': ['react-hotkeys-hook'],
  'dev-mode-integrate': CODEMIRROR_PACKAGE_DEPS,
  'dev-mode-code-mirror': CODEMIRROR_PACKAGE_DEPS,
  'use-content-editor-table-add-controls': ['@tiptap/pm', '@tiptap/react'],
  'use-table-button': ['@tiptap/core'],
  'use-content-editor-source-mode': CODEMIRROR_PACKAGE_DEPS,
  'content-editor/use-menu-navigation': ['@tiptap/react'],
  'content-editor/use-tiptap-editor': ['@tiptap/react'],
  tiptap: TIPTAP_PACKAGE_DEPS,
  'upload-dropzone': ['react-dropzone']
}

function applyNamespaceToTemplateEntry(
  entry: TemplateEntry,
  config: ResolvedBetterstartConfig,
  cwd: string
) {
  const namespace = config.frameworkConfig.next.namespace
  const context = { config, cwd }
  const relPath = typeof entry.relPath === 'function' ? entry.relPath(context) : entry.relPath

  return {
    relPath: applyAdminNamespaceToPath(relPath, namespace),
    content: applyAdminNamespaceToContent(
      applyNextCacheApiForVersion(entry.content(context), config.nextMajorVersion),
      namespace
    )
  }
}

function writeNamespacedFile(srcPath: string, destPath: string, namespace: string): void {
  fs.writeFileSync(
    destPath,
    applyAdminNamespaceToContent(fs.readFileSync(srcPath, 'utf-8'), namespace),
    'utf-8'
  )
}

function copyNamespacedDirectory(srcDir: string, destDir: string, namespace: string): void {
  const entries = fs.readdirSync(srcDir, { withFileTypes: true })

  for (const entry of entries) {
    const namespacedName = applyAdminNamespaceToPath(entry.name, namespace)
    const srcPath = path.join(srcDir, entry.name)
    const destPath = path.join(destDir, namespacedName)

    if (entry.isDirectory()) {
      fsExtra.ensureDirSync(destPath)
      copyNamespacedDirectory(srcPath, destPath, namespace)
      continue
    }

    fsExtra.ensureDirSync(path.dirname(destPath))
    writeNamespacedFile(srcPath, destPath, namespace)
  }
}

interface FileSnapshot {
  existed: boolean
  content?: string
}

interface ProjectPackageJson {
  dependencies?: Record<string, string>
  devDependencies?: Record<string, string>
  optionalDependencies?: Record<string, string>
}

interface AdminShadcnComponentsJson {
  $schema: string
  style: string
  rsc: boolean
  tsx: boolean
  tailwind: {
    config: string
    css: string
    baseColor: string
    cssVariables: boolean
    prefix: string
  }
  iconLibrary: string
  aliases: {
    components: string
    ui: string
    hooks: string
    lib: string
    utils: string
  }
}

function hasPlugin(config: ResolvedBetterstartConfig, pluginId: string): boolean {
  return config.plugins.installed.includes(pluginId)
}

function hasIntegration(config: ResolvedBetterstartConfig, integrationId: string): boolean {
  return config.integrations.installed.includes(integrationId)
}

function readProjectPackageJson(cwd: string): ProjectPackageJson | null {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) return null

  try {
    return JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as ProjectPackageJson
  } catch {
    return null
  }
}

function hasDeclaredPackage(pkg: ProjectPackageJson | null, name: string): boolean {
  if (!pkg) return false
  return (
    Object.hasOwn(pkg.dependencies ?? {}, name) ||
    Object.hasOwn(pkg.devDependencies ?? {}, name) ||
    Object.hasOwn(pkg.optionalDependencies ?? {}, name)
  )
}

/**
 * Template-function components (layout, shared, hooks, utils, etc.)
 * Key = component name used on CLI, value = { relative path in admin/, content generator }
 */
const TEMPLATE_REGISTRY: Record<string, TemplateEntry> = {
  // CSS
  'admin-globals': {
    relPath: 'admin-globals.css',
    content: () => readTemplate('admin-globals.css')
  },
  // Layout
  'admin-providers': {
    relPath: 'components/layouts/admin-providers.tsx',
    content: () => readTemplate('components/layouts/admin-providers.tsx')
  },
  'admin-nav-link': {
    relPath: 'components/layouts/admin-nav-link.tsx',
    content: () => readTemplate('components/layouts/admin-nav-link.tsx')
  },
  'admin-sidebar': {
    relPath: 'components/layouts/admin-sidebar.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar.tsx'),
    dependencies: [
      'admin-sidebar-branding-rsc',
      'admin-sidebar-branding-skeleton',
      'admin-sidebar-nav-link',
      'admin-sidebar-user-menu',
      'admin-sidebar-user-menu-skeleton',
      'sidebar-utils'
    ]
  },
  'admin-sidebar-branding-rsc': {
    relPath: 'components/layouts/admin-sidebar-branding-rsc.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-branding-rsc.tsx'),
    dependencies: ['sidebar-branding']
  },
  'admin-sidebar-branding-skeleton': {
    relPath: 'components/layouts/admin-sidebar-branding-skeleton.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-branding-skeleton.tsx')
  },
  'admin-sidebar-nav-link': {
    relPath: 'components/layouts/admin-sidebar-nav-link.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-nav-link.tsx'),
    dependencies: ['admin-nav-link', 'admin-sidebar-nav-link-skeleton']
  },
  'admin-sidebar-nav-link-skeleton': {
    relPath: 'components/layouts/admin-sidebar-nav-link-skeleton.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-nav-link-skeleton.tsx')
  },
  'admin-sidebar-user-menu': {
    relPath: 'components/layouts/admin-sidebar-user-menu.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-user-menu.tsx'),
    dependencies: ['admin-sign-out']
  },
  'admin-sidebar-user-menu-skeleton': {
    relPath: 'components/layouts/admin-sidebar-user-menu-skeleton.tsx',
    content: () => readTemplate('components/layouts/admin-sidebar-user-menu-skeleton.tsx')
  },
  'sidebar-branding': {
    relPath: 'components/layouts/sidebar-branding.tsx',
    content: () => readTemplate('components/layouts/sidebar-branding.tsx'),
    dependencies: ['app-admin', 'admin-sidebar-branding-skeleton']
  },
  'admin-header': {
    relPath: 'components/layouts/admin-header.tsx',
    content: () => readTemplate('components/layouts/admin-header.tsx')
  },
  'admin-sign-out': {
    relPath: 'components/layouts/admin-sign-out.tsx',
    content: () => readTemplate('components/layouts/admin-sign-out.tsx')
  },
  'content-skeleton': {
    relPath: 'components/layouts/content-skeleton.tsx',
    content: () => readTemplate('components/layouts/content-skeleton.tsx')
  },
  // Shared
  'page-header': {
    relPath: 'components/shared/page-header.tsx',
    content: () => readTemplate('components/shared/page-header.tsx')
  },
  'sort-indicator': {
    relPath: 'components/shared/sort-indicator.tsx',
    content: () => readTemplate('components/shared/sort-indicator.tsx')
  },
  'dev-mode-integrate': {
    relPath: 'components/shared/dev-mode-integrate.tsx',
    content: () => readTemplate('components/shared/dev-mode-integrate.tsx'),
    dependencies: [
      'dev-mode-copyable-code-block',
      'dev-mode-lifecycle-hooks-tab',
      'dev-mode-snippets-tab',
      'dev-mode-types',
      'use-dev-mode-integration'
    ]
  },
  'dev-mode-copyable-code-block': {
    relPath: 'components/shared/dev-mode/copyable-code-block.tsx',
    content: () => readTemplate('components/shared/dev-mode/copyable-code-block.tsx'),
    dependencies: [
      'dev-mode-code-mirror',
      'dev-mode-plain-code-fallback',
      'dev-mode-types',
      'tabs',
      'use-copy-to-clipboard',
      'use-admin-theme'
    ]
  },
  'dev-mode-code-mirror': {
    relPath: 'components/shared/dev-mode/dev-mode-code-mirror.tsx',
    content: () => readTemplate('components/shared/dev-mode/dev-mode-code-mirror.tsx'),
    dependencies: ['dev-mode-types']
  },
  'dev-mode-types': {
    relPath: 'components/shared/dev-mode/dev-mode-types.ts',
    content: () => readTemplate('components/shared/dev-mode/dev-mode-types.ts')
  },
  'dev-mode-plain-code-fallback': {
    relPath: 'components/shared/dev-mode/plain-code-fallback.tsx',
    content: () => readTemplate('components/shared/dev-mode/plain-code-fallback.tsx')
  },
  'dev-mode-lifecycle-hooks-tab': {
    relPath: 'components/shared/dev-mode/lifecycle-hooks-tab.tsx',
    content: () => readTemplate('components/shared/dev-mode/lifecycle-hooks-tab.tsx'),
    dependencies: [
      'accordion',
      'badge',
      'dev-mode-code-block-height-utils',
      'dev-mode-copyable-code-block',
      'dev-mode-types'
    ]
  },
  'dev-mode-snippets-tab': {
    relPath: 'components/shared/dev-mode/snippets-tab.tsx',
    content: () => readTemplate('components/shared/dev-mode/snippets-tab.tsx'),
    dependencies: [
      'accordion',
      'badge',
      'dev-mode-code-block-height-utils',
      'dev-mode-copyable-code-block',
      'dev-mode-types'
    ]
  },
  'delete-dialog': {
    relPath: 'components/shared/delete-dialog.tsx',
    content: () => readTemplate('components/shared/delete-dialog.tsx')
  },
  'entity-metadata': {
    relPath: 'components/shared/entity-metadata.tsx',
    content: () => readTemplate('components/shared/entity-metadata.tsx'),
    dependencies: ['entity-versions-button']
  },
  'entity-versions-button': {
    relPath: 'components/shared/entity-versions/entity-versions-button.tsx',
    content: () => readTemplate('components/shared/entity-versions/entity-versions-button.tsx'),
    dependencies: ['button', 'entity-versions-drawer', 'use-entity-versions']
  },
  'entity-versions-drawer': {
    relPath: 'components/shared/entity-versions/entity-versions-drawer.tsx',
    content: () => readTemplate('components/shared/entity-versions/entity-versions-drawer.tsx'),
    dependencies: ['drawer', 'entity-versions-current-row', 'entity-version-item', 'scroll-area']
  },
  'entity-versions-current-row': {
    relPath: 'components/shared/entity-versions/entity-versions-current-row.tsx',
    content: () =>
      readTemplate('components/shared/entity-versions/entity-versions-current-row.tsx'),
    dependencies: ['avatar', 'badge', 'tooltip', 'user-utils']
  },
  'entity-version-item': {
    relPath: 'components/shared/entity-versions/entity-version-item.tsx',
    content: () => readTemplate('components/shared/entity-versions/entity-version-item.tsx'),
    dependencies: ['avatar', 'badge', 'entity-version-restore-dialog', 'tooltip', 'user-utils']
  },
  'entity-version-restore-dialog': {
    relPath: 'components/shared/entity-versions/entity-version-restore-dialog.tsx',
    content: () =>
      readTemplate('components/shared/entity-versions/entity-version-restore-dialog.tsx'),
    dependencies: ['alert-dialog', 'button']
  },
  'entity-filters-bar': {
    relPath: 'components/shared/entity-filters-bar.tsx',
    content: () => readTemplate('components/shared/entity-filters-bar.tsx'),
    dependencies: [
      'button',
      'date-range-picker',
      'dropdown-menu',
      'search-input',
      'use-page-scroll-threshold'
    ]
  },
  'search-input': {
    relPath: 'components/shared/search-input.tsx',
    content: () => readTemplate('components/shared/search-input.tsx')
  },
  'sort-order-dialog': {
    relPath: 'components/shared/sort-order-dialog.tsx',
    content: () => readTemplate('components/shared/sort-order-dialog.tsx'),
    dependencies: ['sort-order-drag-overlay-item', 'sort-order-item', 'sort-order-types']
  },
  'sort-order-drag-overlay-item': {
    relPath: 'components/shared/sort-order-drag-overlay-item.tsx',
    content: () => readTemplate('components/shared/sort-order-drag-overlay-item.tsx')
  },
  'sort-order-item': {
    relPath: 'components/shared/sort-order-item.tsx',
    content: () => readTemplate('components/shared/sort-order-item.tsx'),
    dependencies: ['sort-order-types']
  },
  'sort-order-types': {
    relPath: 'components/shared/sort-order-types.ts',
    content: () => readTemplate('components/shared/sort-order-types.ts')
  },
  // Media
  'media-preview': {
    relPath: 'components/shared/media/media-preview.tsx',
    content: () => readTemplate('components/shared/media/media-preview.tsx'),
    dependencies: ['use-failed-image-url']
  },
  'media-grid-item': {
    relPath: 'components/shared/media/media-grid-item.tsx',
    content: () => readTemplate('components/shared/media/media-grid-item.tsx'),
    dependencies: ['media-preview']
  },
  'media-grid': {
    relPath: 'components/shared/media/media-grid.tsx',
    content: () => readTemplate('components/shared/media/media-grid.tsx'),
    dependencies: ['media-grid-item']
  },
  'media-grid-pagination': {
    relPath: 'components/shared/media/media-grid-pagination.tsx',
    content: () => readTemplate('components/shared/media/media-grid-pagination.tsx')
  },
  'media-empty-state': {
    relPath: 'components/shared/media/media-empty-state.tsx',
    content: () => readTemplate('components/shared/media/media-empty-state.tsx')
  },
  'edit-media-dialog': {
    relPath: 'components/shared/media/edit-media-dialog.tsx',
    content: () => readTemplate('components/shared/media/edit-media-dialog.tsx'),
    dependencies: ['edit-media-dialog-content']
  },
  'edit-media-dialog-content': {
    relPath: 'components/shared/media/edit-media-dialog-content.tsx',
    content: () => readTemplate('components/shared/media/edit-media-dialog-content.tsx'),
    dependencies: [
      'media-preview',
      'media-delete-drawer',
      'use-failed-image-url',
      'use-copy-to-clipboard'
    ]
  },
  'media-delete-drawer': {
    relPath: 'components/shared/media/media-delete-drawer.tsx',
    content: () => readTemplate('components/shared/media/media-delete-drawer.tsx')
  },
  'media-delete-dialog': {
    relPath: 'components/shared/media/media-delete-dialog.tsx',
    content: () => readTemplate('components/shared/media/media-delete-dialog.tsx')
  },
  'media-gallery-dialog': {
    relPath: 'components/shared/media/media-gallery-dialog.tsx',
    content: () => readTemplate('components/shared/media/media-gallery-dialog.tsx'),
    dependencies: [
      'upload-dropzone',
      'use-upload',
      'use-media',
      'media-grid',
      'media-grid-pagination',
      'media-url-importer'
    ]
  },
  'media-filters-bar': {
    relPath: 'components/shared/media/media-filters-bar.tsx',
    content: () => readTemplate('components/shared/media/media-filters-bar.tsx'),
    dependencies: ['use-page-scroll-threshold']
  },
  'media-url-importer': {
    relPath: 'components/shared/media/media-url-importer.tsx',
    content: () => readTemplate('components/shared/media/media-url-importer.tsx'),
    dependencies: ['media-utils']
  },
  // Data table
  'data-table': {
    relPath: 'components/shared/data-table/data-table.tsx',
    content: () => readTemplate('components/shared/data-table/data-table.tsx'),
    dependencies: ['use-table-utils', 'data-grid', 'data-table-pagination']
  },
  'data-grid': {
    relPath: 'components/shared/data-table/data-grid.tsx',
    content: () => readTemplate('components/shared/data-table/data-grid.tsx')
  },
  'data-table-pagination': {
    relPath: 'components/shared/data-table/data-table-pagination.tsx',
    content: () => readTemplate('components/shared/data-table/data-table-pagination.tsx')
  },
  // Hooks
  'use-upload': {
    relPath: 'hooks/use-upload.ts',
    content: () => readTemplate('hooks/use-upload.ts')
  },
  'use-content-editor-source-mode': {
    relPath: 'hooks/content-editor/use-content-editor-source-mode.tsx',
    content: () => readTemplate('hooks/content-editor/use-content-editor-source-mode.tsx'),
    dependencies: [
      'media-field',
      'use-admin-theme',
      'markdown-utils',
      'content-editor-source-media-utils',
      'tiptap-utils'
    ]
  },
  'use-content-editor': {
    relPath: 'hooks/content-editor/use-content-editor.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor.ts'),
    dependencies: [
      'admin-globals',
      'content-editor-utils',
      'content-editor-rich-extensions',
      'content-editor-editor-view-utils',
      'content-editor-table-bubble-utils',
      'use-content-editor-slash-menu',
      'use-mobile',
      'tiptap',
      'tiptap-utils',
      'content-editor/media-gallery-block',
      'media-field'
    ]
  },
  'use-content-editor-media-insertion': {
    relPath: 'hooks/content-editor/use-content-editor-media-insertion.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-media-insertion.ts'),
    dependencies: [
      'content-editor/use-is-breakpoint',
      'content-editor/use-tiptap-editor',
      'tiptap-utils'
    ]
  },
  'use-blockquote': {
    relPath: 'hooks/content-editor/use-blockquote.ts',
    content: () => readTemplate('hooks/content-editor/use-blockquote.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-code-block': {
    relPath: 'hooks/content-editor/use-code-block.ts',
    content: () => readTemplate('hooks/content-editor/use-code-block.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-color-highlight': {
    relPath: 'hooks/content-editor/use-color-highlight.ts',
    content: () => readTemplate('hooks/content-editor/use-color-highlight.ts'),
    dependencies: [
      'content-editor/use-is-breakpoint',
      'content-editor/use-tiptap-editor',
      'tiptap-utils'
    ]
  },
  'use-heading': {
    relPath: 'hooks/content-editor/use-heading.ts',
    content: () => readTemplate('hooks/content-editor/use-heading.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-heading-dropdown-menu': {
    relPath: 'hooks/content-editor/use-heading-dropdown-menu.ts',
    content: () => readTemplate('hooks/content-editor/use-heading-dropdown-menu.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'use-heading']
  },
  'use-link-popover': {
    relPath: 'hooks/content-editor/use-link-popover.ts',
    content: () => readTemplate('hooks/content-editor/use-link-popover.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-list': {
    relPath: 'hooks/content-editor/use-list.ts',
    content: () => readTemplate('hooks/content-editor/use-list.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-list-dropdown-menu': {
    relPath: 'hooks/content-editor/use-list-dropdown-menu.ts',
    content: () => readTemplate('hooks/content-editor/use-list-dropdown-menu.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'use-list', 'tiptap-utils']
  },
  'use-mark': {
    relPath: 'hooks/content-editor/use-mark.ts',
    content: () => readTemplate('hooks/content-editor/use-mark.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-undo-redo': {
    relPath: 'hooks/content-editor/use-undo-redo.ts',
    content: () => readTemplate('hooks/content-editor/use-undo-redo.ts'),
    dependencies: ['content-editor/use-tiptap-editor', 'tiptap-utils']
  },
  'use-content-editor-table-add-controls': {
    relPath: 'hooks/content-editor/use-content-editor-table-add-controls.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-table-add-controls.ts'),
    dependencies: ['content-editor-table-add-controls-utils', 'tiptap-utils']
  },
  'use-table-button': {
    relPath: 'hooks/content-editor/use-table-button.ts',
    content: () => readTemplate('hooks/content-editor/use-table-button.ts'),
    dependencies: ['content-editor-table-input-utils', 'tiptap-utils']
  },
  'use-content-editor-mobile-toolbar': {
    relPath: 'hooks/content-editor/use-content-editor-mobile-toolbar.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-mobile-toolbar.ts'),
    dependencies: ['use-mobile']
  },
  'use-content-editor-slash-menu': {
    relPath: 'hooks/content-editor/use-content-editor-slash-menu.ts',
    content: () => readTemplate('hooks/content-editor/use-content-editor-slash-menu.ts'),
    dependencies: ['content-editor-slash-commands-utils', 'tiptap-utils']
  },
  'content-editor/use-is-breakpoint': {
    relPath: 'hooks/content-editor/use-is-breakpoint.ts',
    content: () => readTemplate('hooks/content-editor/use-is-breakpoint.ts')
  },
  'content-editor/use-menu-navigation': {
    relPath: 'hooks/content-editor/use-menu-navigation.ts',
    content: () => readTemplate('hooks/content-editor/use-menu-navigation.ts')
  },
  'content-editor/use-tiptap-editor': {
    relPath: 'hooks/content-editor/use-tiptap-editor.ts',
    content: () => readTemplate('hooks/content-editor/use-tiptap-editor.ts')
  },
  'use-copy-to-clipboard': {
    relPath: 'hooks/use-copy-to-clipboard.ts',
    content: () => readTemplate('hooks/use-copy-to-clipboard.ts')
  },
  'use-dev-mode-integration': {
    relPath: 'hooks/use-dev-mode-integration.ts',
    content: () => readTemplate('hooks/use-dev-mode-integration.ts')
  },
  'use-local-storage': {
    relPath: 'hooks/use-local-storage.ts',
    content: () => readTemplate('hooks/use-local-storage.ts')
  },
  'use-admin-theme': {
    relPath: 'hooks/use-admin-theme.tsx',
    content: () => readTemplate('hooks/use-admin-theme.tsx'),
    dependencies: ['theme-system-utils']
  },
  'use-audit-log': {
    relPath: 'hooks/use-audit-log.ts',
    content: () => readTemplate('hooks/use-audit-log.ts'),
    dependencies: ['audit-action']
  },
  'use-users': { relPath: 'hooks/use-users.ts', content: () => readTemplate('hooks/use-users.ts') },
  'use-mobile': {
    relPath: 'hooks/use-mobile.ts',
    content: () => readTemplate('hooks/use-mobile.ts')
  },
  'use-failed-image-url': {
    relPath: 'hooks/use-failed-image-url.ts',
    content: () => readTemplate('hooks/use-failed-image-url.ts')
  },
  'use-entity-versions': {
    relPath: 'hooks/use-entity-versions.ts',
    content: () => readTemplate('hooks/use-entity-versions.ts')
  },
  'use-media': { relPath: 'hooks/use-media.ts', content: () => readTemplate('hooks/use-media.ts') },
  'use-media-filters-bar': {
    relPath: 'hooks/use-media-filters-bar.ts',
    content: () => readTemplate('hooks/use-media-filters-bar.ts')
  },
  'use-table-utils': {
    relPath: 'hooks/use-table-utils.ts',
    content: () => readTemplate('hooks/use-table-utils.ts'),
    dependencies: ['table-utils-core']
  },
  'use-page-boundary-blur-visibility': {
    relPath: 'hooks/use-page-boundary-blur-visibility.ts',
    content: () => readTemplate('hooks/use-page-boundary-blur-visibility.ts'),
    dependencies: ['page-boundary-utils']
  },
  'use-page-scroll-threshold': {
    relPath: 'hooks/use-page-scroll-threshold.ts',
    content: () => readTemplate('hooks/use-page-scroll-threshold.ts')
  },
  // Types
  'types-index': {
    relPath: 'types/index.ts',
    content: () => readTemplate('types/index.ts')
  },
  'auth-types': {
    relPath: 'types/auth.ts',
    content: () => readTemplate('types/auth.ts')
  },
  'navigation-types': {
    relPath: 'types/navigation.ts',
    content: () => readTemplate('types/navigation.ts')
  },
  'table-meta': {
    relPath: 'types/table-meta.ts',
    content: () => readTemplate('types/table-meta.ts')
  },
  // Utils
  'app-admin': {
    relPath: ({ config }) =>
      `utils/app/${resolveAdminNamespace(config.frameworkConfig.next.namespace).camel}.ts`,
    displayPath: 'utils/app/<namespaceCamel>.ts',
    content: ({ cwd, config }) =>
      adminDataTemplate(detectProjectName(cwd), config.frameworkConfig.next.namespace)
  },
  cn: { relPath: 'utils/shared/cn.ts', content: () => readTemplate('utils/shared/cn.ts') },
  'auth-roles-utils': {
    relPath: 'utils/auth/roles.ts',
    content: () => readTemplate('utils/auth/roles.ts')
  },
  'audit-utils': {
    relPath: 'utils/audit/audit.ts',
    content: () => readTemplate('utils/audit/audit.ts')
  },
  'date-utils': {
    relPath: 'utils/date/date.ts',
    content: () => readTemplate('utils/date/date.ts')
  },
  'markdown-utils': {
    relPath: 'utils/editor/markdown.ts',
    content: () => readTemplate('utils/editor/markdown.ts')
  },
  'content-editor-utils': {
    relPath: 'utils/editor/content-editor.ts',
    content: () => readTemplate('utils/editor/content-editor.ts')
  },
  'content-editor-rich-extensions': {
    relPath: 'utils/editor/content-editor-rich-extensions.ts',
    content: () => readTemplate('utils/editor/content-editor-rich-extensions.ts')
  },
  'content-editor-editor-view-utils': {
    relPath: 'utils/editor/editor-view.ts',
    content: () => readTemplate('utils/editor/editor-view.ts')
  },
  'content-editor-node-attrs-utils': {
    relPath: 'utils/editor/node-attrs.ts',
    content: () => readTemplate('utils/editor/node-attrs.ts')
  },
  'content-editor-source-media-utils': {
    relPath: 'utils/editor/source-media.ts',
    content: () => readTemplate('utils/editor/source-media.ts')
  },
  'content-editor-slash-commands-utils': {
    relPath: 'utils/editor/slash-commands.ts',
    content: () => readTemplate('utils/editor/slash-commands.ts')
  },
  'content-editor-table-add-controls-utils': {
    relPath: 'utils/editor/table-add-controls.ts',
    content: () => readTemplate('utils/editor/table-add-controls.ts')
  },
  'content-editor-table-bubble-utils': {
    relPath: 'utils/editor/table-bubble.ts',
    content: () => readTemplate('utils/editor/table-bubble.ts')
  },
  'content-editor-table-input-utils': {
    relPath: 'utils/editor/table-input.ts',
    content: () => readTemplate('utils/editor/table-input.ts')
  },
  'content-editor-task-item-utils': {
    relPath: 'utils/editor/task-item.ts',
    content: () => readTemplate('utils/editor/task-item.ts')
  },
  'email-form-delivery-utils': {
    relPath: 'utils/email/form-delivery.ts',
    content: () => readTemplate('utils/email/form-delivery.ts')
  },
  'form-list-field-utils': {
    relPath: 'utils/form/list-field.ts',
    content: () => readTemplate('utils/form/list-field.ts')
  },
  'media-fallback-utils': {
    relPath: 'utils/media/fallback.ts',
    content: () => readTemplate('utils/media/fallback.ts')
  },
  'media-utils': {
    relPath: 'utils/media/media.ts',
    content: () => readTemplate('utils/media/media.ts')
  },
  'media-query-utils': {
    relPath: 'utils/media/query.ts',
    content: () => readTemplate('utils/media/query.ts')
  },
  'navigation-order-utils': {
    relPath: 'utils/navigation/order.ts',
    content: () => readTemplate('utils/navigation/order.ts')
  },
  seo: { relPath: 'utils/seo/seo.ts', content: () => readTemplate('utils/seo/seo.ts') },
  'sidebar-utils': {
    relPath: 'utils/navigation/sidebar.ts',
    content: () => readTemplate('utils/navigation/sidebar.ts')
  },
  'page-boundary-utils': {
    relPath: 'utils/page/boundary.ts',
    content: () => readTemplate('utils/page/boundary.ts')
  },
  'storage-local-utils': {
    relPath: 'utils/storage/local.ts',
    content: () => readTemplate('utils/storage/local.ts')
  },
  'table-utils-core': {
    relPath: 'utils/table/table.ts',
    content: () => readTemplate('utils/table/table.ts')
  },
  'text-utils': {
    relPath: 'utils/text/text.ts',
    content: () => readTemplate('utils/text/text.ts')
  },
  'theme-system-utils': {
    relPath: 'utils/theme/system.ts',
    content: () => readTemplate('utils/theme/system.ts')
  },
  'tiptap-utils': {
    relPath: 'utils/editor/tiptap.ts',
    content: () => readTemplate('utils/editor/tiptap.ts')
  },
  'upload-remote-utils': {
    relPath: 'utils/upload/remote.ts',
    content: () => readTemplate('utils/upload/remote.ts')
  },
  'upload-utils': {
    relPath: 'utils/upload/upload.ts',
    content: () => readTemplate('utils/upload/upload.ts')
  },
  'user-utils': {
    relPath: 'utils/user/user.ts',
    content: () => readTemplate('utils/user/user.ts')
  },
  validation: {
    relPath: 'utils/validation/validation.ts',
    content: () => readTemplate('utils/validation/validation.ts')
  },
  webhook: {
    relPath: 'utils/webhook/webhook.ts',
    content: () => readTemplate('utils/webhook/webhook.ts')
  },
  'dev-mode-code-block-height-utils': {
    relPath: 'utils/dev-mode/code-block-height.ts',
    content: () => readTemplate('utils/dev-mode/code-block-height.ts')
  },
  'lifecycle-hooks': {
    relPath: 'lib/lifecycle-hooks/index.ts',
    content: () => readTemplate('lib/lifecycle-hooks/index.ts'),
    dependencies: [
      'audit-register-action',
      'lifecycle-hooks-types',
      'lifecycle-hooks-registry',
      'lifecycle-hooks-runner',
      'lifecycle-hooks-register',
      'lifecycle-hooks-register-local'
    ]
  },
  'lifecycle-hooks-types': {
    relPath: 'lib/lifecycle-hooks/types.ts',
    content: () => readTemplate('lib/lifecycle-hooks/types.ts'),
    dependencies: ['audit-action-types']
  },
  'lifecycle-hooks-registry': {
    relPath: 'lib/lifecycle-hooks/registry.ts',
    content: () => readTemplate('lib/lifecycle-hooks/registry.ts')
  },
  'lifecycle-hooks-runner': {
    relPath: 'lib/lifecycle-hooks/runner.ts',
    content: () => readTemplate('lib/lifecycle-hooks/runner.ts')
  },
  'lifecycle-hooks-register': {
    relPath: 'lib/lifecycle-hooks/register.ts',
    content: () => readTemplate('lib/lifecycle-hooks/register.ts')
  },
  'lifecycle-hooks-register-local': {
    relPath: 'lib/lifecycle-hooks/register.local.ts',
    content: () => readTemplate('lib/lifecycle-hooks/register.local.ts'),
    preserveExisting: true
  },
  'mailchimp-integration': {
    relPath: 'lib/actions/mailchimp/add-to-mailchimp-audience.ts',
    requiredIntegration: 'mailchimp',
    content: () => readIntegrationTemplate('mailchimp', 'actions/mailchimp.ts')
  },
  // Auth
  'auth-server': {
    relPath: 'lib/actions/auth/server.ts',
    content: () => readTemplate('lib/actions/auth/auth.ts')
  },
  'auth-client': {
    relPath: 'lib/actions/auth/client.ts',
    content: () => readTemplate('lib/actions/auth/client.ts')
  },
  'auth-session': {
    relPath: 'lib/actions/auth/session.ts',
    content: () => readTemplate('lib/actions/auth/middleware.ts'),
    dependencies: ['auth-roles-utils']
  },
  'admin-layout': {
    relPath: 'app/(admin)/admin/layout.tsx',
    content: () => readTemplate('pages/admin-layout.tsx'),
    base: 'cwd',
    dependencies: ['admin-globals', 'admin-providers', 'cn']
  },
  'auth-layout': {
    relPath: 'app/(admin)/admin/(authenticated)/layout.tsx',
    content: () => readTemplate('pages/authenticated-layout.tsx'),
    base: 'cwd',
    dependencies: ['auth-gate-rsc']
  },
  'auth-gate-rsc': {
    relPath: 'app/(admin)/admin/(authenticated)/auth-gate-rsc.tsx',
    content: () => readTemplate('pages/auth-gate-rsc.tsx'),
    base: 'cwd',
    dependencies: ['admin-sidebar', 'auth-session']
  },
  'account-layout': {
    relPath: 'app/(admin)/admin/(account)/layout.tsx',
    content: () => readTemplate('pages/account-layout.tsx'),
    base: 'cwd',
    dependencies: ['account-shell-rsc']
  },
  'account-shell-rsc': {
    relPath: 'app/(admin)/admin/(account)/account-shell-rsc.tsx',
    content: () => readTemplate('pages/account-shell-rsc.tsx'),
    base: 'cwd',
    dependencies: [
      'admin-sidebar',
      'app-admin',
      'auth-roles-utils',
      'auth-session',
      'minimal-account-shell'
    ]
  },
  'minimal-account-shell': {
    relPath: 'app/(admin)/admin/(account)/minimal-account-shell.tsx',
    content: () => readTemplate('pages/minimal-account-shell.tsx'),
    base: 'cwd',
    dependencies: ['admin-sign-out']
  },
  'profile-page': {
    relPath: 'app/(admin)/admin/(account)/profile/page.tsx',
    content: () => readTemplate('pages/profile/profile-page.tsx'),
    base: 'cwd'
  },
  'profile-form': {
    relPath: 'app/(admin)/admin/(account)/profile/profile-form.tsx',
    content: () => readTemplate('pages/profile/profile-form.tsx'),
    base: 'cwd',
    dependencies: ['profile-action']
  },
  // Password reset
  'login-page': {
    relPath: 'app/(admin)/admin/(auth)/login/page.tsx',
    content: () => readTemplate('pages/login-page.tsx'),
    base: 'cwd',
    dependencies: ['login-page-rsc', 'login-page-skeleton']
  },
  'login-page-rsc': {
    relPath: 'app/(admin)/admin/(auth)/login/login-page-rsc.tsx',
    content: () => readTemplate('pages/login-page-rsc.tsx'),
    base: 'cwd',
    dependencies: ['auth-roles-utils', 'auth-session', 'login-form']
  },
  'login-page-skeleton': {
    relPath: 'app/(admin)/admin/(auth)/login/login-page-skeleton.tsx',
    content: () => readTemplate('pages/login-page-skeleton.tsx'),
    base: 'cwd'
  },
  'login-form': {
    relPath: 'app/(admin)/admin/(auth)/login/login-form.tsx',
    content: () => readTemplate('pages/login-form.tsx'),
    base: 'cwd',
    dependencies: ['auth-roles-utils']
  },
  'forgot-password-page': {
    relPath: 'app/(admin)/admin/(auth)/forgot-password/page.tsx',
    content: () => readTemplate('pages/forgot-password-page.tsx'),
    base: 'cwd',
    dependencies: ['forgot-password-page-skeleton']
  },
  'forgot-password-page-skeleton': {
    relPath: 'app/(admin)/admin/(auth)/forgot-password/forgot-password-page-skeleton.tsx',
    content: () => readTemplate('pages/forgot-password-page-skeleton.tsx'),
    base: 'cwd'
  },
  'forgot-password-form': {
    relPath: 'app/(admin)/admin/(auth)/forgot-password/forgot-password-form.tsx',
    content: () => readTemplate('pages/forgot-password-form.tsx'),
    base: 'cwd'
  },
  'reset-password-page': {
    relPath: 'app/(admin)/admin/(auth)/reset-password/page.tsx',
    content: () => readTemplate('pages/reset-password-page.tsx'),
    base: 'cwd',
    dependencies: ['reset-password-page-skeleton']
  },
  'reset-password-page-skeleton': {
    relPath: 'app/(admin)/admin/(auth)/reset-password/reset-password-page-skeleton.tsx',
    content: () => readTemplate('pages/reset-password-page-skeleton.tsx'),
    base: 'cwd'
  },
  'reset-password-form': {
    relPath: 'app/(admin)/admin/(auth)/reset-password/reset-password-form.tsx',
    content: () => readTemplate('pages/reset-password-form.tsx'),
    base: 'cwd'
  },
  'users-page': {
    relPath: 'app/(admin)/admin/(authenticated)/users/page.tsx',
    content: () => readTemplate('pages/users/users-page.tsx'),
    base: 'cwd',
    dependencies: ['users-page-skeleton', 'users-page-content']
  },
  'users-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/users/users-page-skeleton.tsx',
    content: () => readTemplate('pages/users/users-page-skeleton.tsx'),
    base: 'cwd'
  },
  'users-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/users/users-page-content.tsx',
    content: () => readTemplate('pages/users/users-page-content.tsx'),
    base: 'cwd',
    dependencies: ['users-columns', 'users-table', 'create-user-dialog', 'entity-filters-bar']
  },
  'users-columns': {
    relPath: 'app/(admin)/admin/(authenticated)/users/columns.tsx',
    content: () => readTemplate('pages/users/columns.tsx'),
    base: 'cwd',
    dependencies: ['user-utils', 'delete-user-dialog', 'edit-role-dialog']
  },
  'users-table': {
    relPath: 'app/(admin)/admin/(authenticated)/users/users-table.tsx',
    content: () => readTemplate('pages/users/users-table.tsx'),
    base: 'cwd',
    dependencies: ['use-table-utils', 'data-grid', 'data-table-pagination', 'user-utils']
  },
  'create-user-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/create-user-dialog.tsx',
    content: () => readTemplate('pages/users/create-user-dialog.tsx'),
    base: 'cwd'
  },
  'delete-user-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/delete-user-dialog.tsx',
    content: () => readTemplate('pages/users/delete-user-dialog.tsx'),
    base: 'cwd'
  },
  'edit-role-dialog': {
    relPath: 'app/(admin)/admin/(authenticated)/users/edit-role-dialog.tsx',
    content: () => readTemplate('pages/users/edit-role-dialog.tsx'),
    base: 'cwd'
  },
  // Lib
  'email-index': {
    relPath: 'lib/actions/email/index.ts',
    content: (context) =>
      context
        ? renderCoreEmailProviderIndex(context.config)
        : readTemplate('lib/actions/email/index.ts')
  },
  'email-provider': {
    relPath: 'lib/actions/email/provider.ts',
    content: (context) =>
      context
        ? renderCoreEmailProvider(context.config)
        : readTemplate('lib/actions/email/provider.ts')
  },
  'email-types': {
    relPath: 'lib/actions/email/types.ts',
    content: () => readTemplate('lib/actions/email/types.ts')
  },
  'email-is-delivery-configured': {
    relPath: 'lib/actions/email/is-email-delivery-configured.ts',
    content: () => readTemplate('lib/actions/email/is-email-delivery-configured.ts')
  },
  'email-send-email': {
    relPath: 'lib/actions/email/send-email.ts',
    content: () => readTemplate('lib/actions/email/send-email.ts')
  },
  'email-send-password-reset-email': {
    relPath: 'lib/actions/email/send-password-reset-email.ts',
    content: () => readTemplate('lib/actions/email/send-password-reset-email.ts')
  },
  'email-form-delivery': {
    relPath: 'lib/actions/email/form-delivery.ts',
    content: () => readTemplate('lib/actions/email/form-delivery.ts'),
    dependencies: ['email-form-delivery-utils']
  },
  'email-none-provider': {
    relPath: 'lib/actions/email/none.ts',
    content: () => readTemplate('lib/actions/email/none.ts')
  },
  'email-resend-provider': {
    relPath: 'lib/actions/resend/provider.ts',
    requiredIntegration: 'resend',
    content: () => readIntegrationTemplate('resend', 'actions/resend.ts')
  },
  'storage-index': {
    relPath: 'lib/actions/storage/index.ts',
    content: (context) =>
      context
        ? renderCoreStorageProviderIndex(context.config)
        : readTemplate('lib/actions/storage/index.ts')
  },
  'storage-provider': {
    relPath: 'lib/actions/storage/provider.ts',
    content: (context) =>
      context
        ? renderCoreStorageProvider(context.config)
        : readTemplate('lib/actions/storage/provider.ts')
  },
  'storage-types': {
    relPath: 'lib/actions/storage/types.ts',
    content: () => readTemplate('lib/actions/storage/types.ts')
  },
  'storage-save-upload': {
    relPath: 'lib/actions/storage/save-upload.ts',
    content: () => readTemplate('lib/actions/storage/save-upload.ts')
  },
  'storage-local-provider': {
    relPath: 'lib/actions/storage/local.ts',
    content: () => readTemplate('lib/actions/storage/local.ts'),
    dependencies: ['storage-local-utils']
  },
  'storage-r2-provider': {
    relPath: 'lib/actions/r2/provider.ts',
    requiredIntegration: 'r2',
    content: () => readIntegrationTemplate('r2', 'actions/r2.ts')
  },
  'form-settings-action': {
    relPath: 'lib/actions/forms/index.ts',
    content: () => readTemplate('lib/actions/forms/index.ts'),
    dependencies: [
      'form-settings-types',
      'get-form-settings-action',
      'upsert-form-settings-action',
      'get-all-form-settings-action',
      'test-form-webhook-action'
    ]
  },
  'form-settings-types': {
    relPath: 'lib/actions/forms/types.ts',
    content: () => readTemplate('lib/actions/forms/types.ts')
  },
  'get-form-settings-action': {
    relPath: 'lib/actions/forms/get-form-settings.ts',
    content: () => readTemplate('lib/actions/forms/get-form-settings.ts'),
    dependencies: ['form-settings-types']
  },
  'upsert-form-settings-action': {
    relPath: 'lib/actions/forms/upsert-form-settings.ts',
    content: () => readTemplate('lib/actions/forms/upsert-form-settings.ts'),
    dependencies: ['form-settings-types']
  },
  'get-all-form-settings-action': {
    relPath: 'lib/actions/forms/get-all-form-settings.ts',
    content: () => readTemplate('lib/actions/forms/get-all-form-settings.ts'),
    dependencies: ['form-settings-types']
  },
  'test-form-webhook-action': {
    relPath: 'lib/actions/forms/test-form-webhook.ts',
    content: () => readTemplate('lib/actions/forms/test-form-webhook.ts')
  },
  'entity-versions-action': {
    relPath: 'lib/actions/entity-versions/index.ts',
    content: () => readTemplate('lib/actions/entity-versions/index.ts'),
    dependencies: [
      'entity-versions-types',
      'get-entity-versions-action',
      'internal-create-entity-version-action',
      'internal-delete-entity-versions-action'
    ]
  },
  'entity-versions-types': {
    relPath: 'lib/actions/entity-versions/types.ts',
    content: () => readTemplate('lib/actions/entity-versions/types.ts')
  },
  'get-entity-versions-action': {
    relPath: 'lib/actions/entity-versions/get-entity-versions.ts',
    content: () => readTemplate('lib/actions/entity-versions/get-entity-versions.ts'),
    dependencies: ['entity-versions-types']
  },
  'internal-create-entity-version-action': {
    relPath: 'lib/actions/entity-versions/internal-create-entity-version.ts',
    content: () => readTemplate('lib/actions/entity-versions/internal-create-entity-version.ts'),
    dependencies: ['entity-versions-types']
  },
  'internal-delete-entity-versions-action': {
    relPath: 'lib/actions/entity-versions/internal-delete-entity-versions.ts',
    content: () => readTemplate('lib/actions/entity-versions/internal-delete-entity-versions.ts')
  },
  'audit-action': {
    relPath: 'lib/actions/audit/index.ts',
    content: () => readTemplate('lib/actions/audit/index.ts'),
    dependencies: [
      'audit-action-types',
      'audit-cleanup-action',
      'audit-get-action',
      'audit-record-event-action',
      'audit-register-action',
      'audit-request-metadata-action'
    ]
  },
  'audit-action-types': {
    relPath: 'lib/actions/audit/types.ts',
    content: () => readTemplate('lib/actions/audit/types.ts')
  },
  'audit-request-metadata-action': {
    relPath: 'lib/actions/audit/request-metadata.ts',
    content: () => readTemplate('lib/actions/audit/request-metadata.ts')
  },
  'audit-record-event-action': {
    relPath: 'lib/actions/audit/record-audit-event.ts',
    content: () => readTemplate('lib/actions/audit/record-audit-event.ts'),
    dependencies: ['audit-action-types']
  },
  'audit-register-action': {
    relPath: 'lib/actions/audit/register.ts',
    content: () => readTemplate('lib/actions/audit/register.ts'),
    dependencies: ['audit-record-event-action']
  },
  'audit-get-action': {
    relPath: 'lib/actions/audit/get-audit-logs.ts',
    content: () => readTemplate('lib/actions/audit/get-audit-logs.ts'),
    dependencies: ['audit-action-types']
  },
  'audit-cleanup-action': {
    relPath: 'lib/actions/audit/cleanup-audit-logs.ts',
    content: () => readTemplate('lib/actions/audit/cleanup-audit-logs.ts'),
    dependencies: ['audit-action-types', 'audit-request-metadata-action', 'lifecycle-hooks']
  },
  'upload-action': {
    relPath: 'lib/actions/upload/index.ts',
    content: () => readTemplate('lib/actions/upload/index.ts'),
    dependencies: [
      'upload-action-types',
      'upload-remote-utils',
      'upload-files-action',
      'upload-file-action',
      'upload-media-from-url-action',
      'upload-image-from-url-action'
    ]
  },
  'upload-action-types': {
    relPath: 'lib/actions/upload/types.ts',
    content: () => readTemplate('lib/actions/upload/types.ts')
  },
  'upload-files-action': {
    relPath: 'lib/actions/upload/upload-files.ts',
    content: () => readTemplate('lib/actions/upload/upload-files.ts')
  },
  'upload-file-action': {
    relPath: 'lib/actions/upload/upload-file.ts',
    content: () => readTemplate('lib/actions/upload/upload-file.ts')
  },
  'upload-media-from-url-action': {
    relPath: 'lib/actions/upload/upload-media-from-url.ts',
    content: () => readTemplate('lib/actions/upload/upload-media-from-url.ts')
  },
  'upload-image-from-url-action': {
    relPath: 'lib/actions/upload/upload-image-from-url.ts',
    content: () => readTemplate('lib/actions/upload/upload-image-from-url.ts')
  },
  'users-action': {
    relPath: 'lib/actions/users/index.ts',
    content: () => readTemplate('lib/actions/users/index.ts'),
    dependencies: [
      'users-action-types',
      'create-user-action',
      'get-users-action',
      'update-user-role-action',
      'delete-user-action'
    ]
  },
  'users-action-types': {
    relPath: 'lib/actions/users/types.ts',
    content: () => readTemplate('lib/actions/users/types.ts')
  },
  'create-user-action': {
    relPath: 'lib/actions/users/create-user.ts',
    content: () => readTemplate('lib/actions/users/create-user.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'audit-utils',
      'lifecycle-hooks',
      'users-action-types'
    ]
  },
  'get-users-action': {
    relPath: 'lib/actions/users/get-users.ts',
    content: () => readTemplate('lib/actions/users/get-users.ts'),
    dependencies: ['users-action-types']
  },
  'update-user-role-action': {
    relPath: 'lib/actions/users/update-user-role.ts',
    content: () => readTemplate('lib/actions/users/update-user-role.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'users-action-types']
  },
  'delete-user-action': {
    relPath: 'lib/actions/users/delete-user.ts',
    content: () => readTemplate('lib/actions/users/delete-user.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'users-action-types']
  },
  'media-action': {
    relPath: 'lib/actions/media/index.ts',
    content: () => readTemplate('lib/actions/media/index.ts'),
    dependencies: [
      'media-action-types',
      'media-query-utils',
      'get-media-action',
      'get-media-by-id-action',
      'get-media-by-ids-action',
      'create-media-action',
      'update-media-action',
      'delete-media-action',
      'delete-media-bulk-action'
    ]
  },
  'media-action-types': {
    relPath: 'lib/actions/media/types.ts',
    content: () => readTemplate('lib/actions/media/types.ts')
  },
  'get-media-action': {
    relPath: 'lib/actions/media/get-media.ts',
    content: () => readTemplate('lib/actions/media/get-media.ts'),
    dependencies: ['media-action-types']
  },
  'get-media-by-id-action': {
    relPath: 'lib/actions/media/get-media-by-id.ts',
    content: () => readTemplate('lib/actions/media/get-media-by-id.ts'),
    dependencies: ['media-action-types']
  },
  'get-media-by-ids-action': {
    relPath: 'lib/actions/media/get-media-by-ids.ts',
    content: () => readTemplate('lib/actions/media/get-media-by-ids.ts'),
    dependencies: ['media-action-types']
  },
  'create-media-action': {
    relPath: 'lib/actions/media/create-media.ts',
    content: () => readTemplate('lib/actions/media/create-media.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'audit-utils',
      'lifecycle-hooks',
      'media-action-types'
    ]
  },
  'update-media-action': {
    relPath: 'lib/actions/media/update-media.ts',
    content: () => readTemplate('lib/actions/media/update-media.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'audit-utils',
      'lifecycle-hooks',
      'media-action-types'
    ]
  },
  'delete-media-action': {
    relPath: 'lib/actions/media/delete-media.ts',
    content: () => readTemplate('lib/actions/media/delete-media.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'media-action-types']
  },
  'delete-media-bulk-action': {
    relPath: 'lib/actions/media/delete-media-bulk.ts',
    content: () => readTemplate('lib/actions/media/delete-media-bulk.ts'),
    dependencies: ['audit-request-metadata-action', 'lifecycle-hooks', 'media-action-types']
  },
  'profile-action': {
    relPath: 'lib/actions/profile/index.ts',
    content: () => readTemplate('lib/actions/profile/index.ts'),
    dependencies: [
      'profile-action-types',
      'is-email-configured-action',
      'invalidate-users-cache-action',
      'update-email-action'
    ]
  },
  'profile-action-types': {
    relPath: 'lib/actions/profile/types.ts',
    content: () => readTemplate('lib/actions/profile/types.ts')
  },
  'is-email-configured-action': {
    relPath: 'lib/actions/profile/is-email-configured.ts',
    content: () => readTemplate('lib/actions/profile/is-email-configured.ts')
  },
  'invalidate-users-cache-action': {
    relPath: 'lib/actions/profile/invalidate-users-cache.ts',
    content: () => readTemplate('lib/actions/profile/invalidate-users-cache.ts'),
    dependencies: ['users-action-types']
  },
  'update-email-action': {
    relPath: 'lib/actions/profile/update-email.ts',
    content: () => readTemplate('lib/actions/profile/update-email.ts'),
    dependencies: [
      'audit-request-metadata-action',
      'lifecycle-hooks',
      'profile-action-types',
      'invalidate-users-cache-action',
      'users-action-types'
    ]
  },
  // API routes
  'auth-route': {
    relPath: 'app/(admin)/api/admin/auth/[...all]/route.ts',
    content: () => readTemplate('api/auth-route.ts'),
    base: 'cwd',
    dependencies: ['auth-server']
  },
  'upload-route': {
    relPath: 'app/(admin)/api/admin/upload/route.ts',
    content: () => readTemplate('api/upload-route.ts'),
    base: 'cwd',
    dependencies: ['media-action-types']
  },
  'media-page': {
    relPath: 'app/(admin)/admin/(authenticated)/media/page.tsx',
    content: () => readTemplate('pages/media/media-page.tsx'),
    base: 'cwd',
    dependencies: ['media-page-skeleton']
  },
  'media-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/media/media-page-skeleton.tsx',
    content: () => readTemplate('pages/media/media-page-skeleton.tsx'),
    base: 'cwd'
  },
  'media-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/media/media-page-content.tsx',
    content: () => readTemplate('pages/media/media-page-content.tsx'),
    base: 'cwd',
    dependencies: ['use-media', 'media-grid-pagination']
  },
  'audit-log-page': {
    relPath: 'app/(admin)/admin/(authenticated)/system/audit-log/page.tsx',
    content: () => readTemplate('pages/audit-log/audit-log-page.tsx'),
    base: 'cwd',
    dependencies: ['audit-log-page-content', 'audit-log-page-skeleton']
  },
  'audit-log-page-skeleton': {
    relPath: 'app/(admin)/admin/(authenticated)/system/audit-log/audit-log-page-skeleton.tsx',
    content: () => readTemplate('pages/audit-log/audit-log-page-skeleton.tsx'),
    base: 'cwd',
    dependencies: ['page-header', 'skeleton']
  },
  'audit-log-page-content': {
    relPath: 'app/(admin)/admin/(authenticated)/system/audit-log/audit-log-page-content.tsx',
    content: () => readTemplate('pages/audit-log/audit-log-page-content.tsx'),
    base: 'cwd',
    dependencies: [
      'audit-log-detail-drawer',
      'badge',
      'button',
      'card',
      'data-grid',
      'data-table-pagination',
      'entity-filters-bar',
      'page-header',
      'sort-indicator',
      'use-table-utils',
      'use-audit-log'
    ]
  },
  'audit-log-detail-drawer': {
    relPath: 'app/(admin)/admin/(authenticated)/system/audit-log/audit-log-detail-drawer.tsx',
    content: () => readTemplate('pages/audit-log/audit-log-detail-drawer.tsx'),
    base: 'cwd',
    dependencies: ['audit-log-detail-row', 'badge', 'drawer', 'scroll-area']
  },
  'audit-log-detail-row': {
    relPath: 'app/(admin)/admin/(authenticated)/system/audit-log/audit-log-detail-row.tsx',
    content: () => readTemplate('pages/audit-log/audit-log-detail-row.tsx'),
    base: 'cwd'
  }
}

/**
 * Get all available static shadcn UI component names from the shared React Admin asset pack.
 */
function getStaticUiComponents(): string[] {
  return getStaticAssetComponents('ui')
}

function getStaticUiComponentEntries(): StaticAssetComponent[] {
  return getStaticAssetComponentEntries('ui')
}

/**
 * Get all available static BetterStart custom component names from the shared React Admin asset pack.
 */
function getStaticCustomComponents(): string[] {
  return getStaticAssetComponents('custom')
}

function getStaticCustomComponentEntries(): StaticAssetComponent[] {
  return getStaticAssetComponentEntries('custom')
}

function getStaticAssetComponents(assetDirectory: 'ui' | 'custom'): string[] {
  return getStaticAssetComponentEntries(assetDirectory).map((entry) => entry.name)
}

function getStaticAssetComponentEntries(assetDirectory: 'ui' | 'custom'): StaticAssetComponent[] {
  const assetDir = resolveCliAssetPath('shared-assets', 'react-admin', assetDirectory)
  if (!fs.existsSync(assetDir)) return []

  const components: StaticAssetComponent[] = []

  for (const entry of fs.readdirSync(assetDir, { withFileTypes: true })) {
    if (entry.isFile() && (entry.name.endsWith('.tsx') || entry.name.endsWith('.ts'))) {
      components.push({
        name: entry.name.replace(/\.(tsx|ts)$/, ''),
        file: entry.name
      })
      continue
    }

    if (!entry.isDirectory()) {
      continue
    }

    const indexFile = ['index.tsx', 'index.ts'].find((file) =>
      fs.existsSync(path.join(assetDir, entry.name, file))
    )
    if (indexFile) {
      components.push({
        name: entry.name,
        file: path.join(entry.name, indexFile)
      })
    }
  }

  return components.sort((a, b) => a.name.localeCompare(b.name))
}

function findStaticAssetFile(assetDir: string, componentName: string): string | undefined {
  if (!fs.existsSync(assetDir)) return undefined

  const isNestedComponentName = /[\\/]/.test(componentName)
  const nestedComponentName = componentName.split(/[\\/]/).filter(Boolean).join(path.sep)
  if (
    isNestedComponentName &&
    nestedComponentName &&
    !path.isAbsolute(componentName) &&
    !nestedComponentName.split(path.sep).includes('..')
  ) {
    for (const extension of ['.tsx', '.ts']) {
      const relPath = `${nestedComponentName}${extension}`
      const filePath = path.join(assetDir, relPath)
      if (fs.existsSync(filePath) && fs.statSync(filePath).isFile()) {
        return relPath
      }
    }
  }

  if (!isNestedComponentName && !componentName.includes('..') && !path.isAbsolute(componentName)) {
    for (const extension of ['.tsx', '.ts']) {
      const relPath = path.join(componentName, `index${extension}`)
      const filePath = path.join(assetDir, relPath)
      if (fs.existsSync(filePath) && fs.statSync(filePath).isFile()) {
        return relPath
      }
    }
  }

  return fs
    .readdirSync(assetDir, { withFileTypes: true })
    .find(
      (entry) =>
        entry.isFile() &&
        (entry.name.endsWith('.tsx') || entry.name.endsWith('.ts')) &&
        entry.name.replace(/\.(tsx|ts)$/, '') === componentName
    )?.name
}

function getAllComponentNames(): string[] {
  const staticUi = getStaticUiComponents()
  const staticCustom = getStaticCustomComponents()
  const templateKeys = Object.keys(TEMPLATE_REGISTRY)
  return [...new Set([...staticUi, ...staticCustom, ...templateKeys, 'tiptap'])].sort()
}

function getAllComponentNamesForConfig(config: ResolvedBetterstartConfig): string[] {
  const staticUi = getStaticUiComponents()
  const staticCustom = getStaticCustomComponents()
  const templateKeys = Object.entries(TEMPLATE_REGISTRY)
    .filter(
      ([, entry]) =>
        (!entry.requiredPlugin || hasPlugin(config, entry.requiredPlugin)) &&
        (!entry.requiredIntegration || hasIntegration(config, entry.requiredIntegration))
    )
    .map(([name]) => name)

  return [...new Set([...staticUi, ...staticCustom, ...templateKeys, 'tiptap'])].sort()
}

export async function runUpdateCommand(
  components: string[],
  options: UpdateCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const normalizedOnly = normalizeShadcnPresetOnly(options.only)

  validateShadcnPresetOptions(components, options)

  if (options.list) {
    const all = getAllComponentNames()
    clack.intro('Available components')

    const uiComponents = getStaticUiComponentEntries()
    const customComponents = getStaticCustomComponentEntries()
    const templateKeys = Object.keys(TEMPLATE_REGISTRY).sort()

    // UI components table
    console.log()
    console.log(`  Shadcn UI Components (${uiComponents.length})`)
    console.log(`  ${'─'.repeat(50)}`)
    console.log(`  ${'Name'.padEnd(28)} ${'Path'}`)
    console.log(`  ${'─'.repeat(50)}`)
    for (const component of uiComponents) {
      console.log(`  ${component.name.padEnd(28)} components/ui/${component.file}`)
    }

    // Custom components table
    console.log()
    console.log(`  Custom Components (${customComponents.length})`)
    console.log(`  ${'─'.repeat(56)}`)
    console.log(`  ${'Name'.padEnd(28)} ${'Path'}`)
    console.log(`  ${'─'.repeat(56)}`)
    for (const component of customComponents) {
      console.log(`  ${component.name.padEnd(28)} components/custom/${component.file}`)
    }

    // Template components table
    console.log()
    console.log(`  Template Components (${templateKeys.length})`)
    console.log(`  ${'─'.repeat(56)}`)
    console.log(`  ${'Name'.padEnd(28)} ${'Path'}`)
    console.log(`  ${'─'.repeat(56)}`)
    for (const name of templateKeys) {
      const entry = TEMPLATE_REGISTRY[name]
      const relPath = entry.displayPath ?? entry.relPath
      console.log(`  ${name.padEnd(28)} ${relPath}`)
    }

    // Special
    console.log()
    console.log(`  Special`)
    console.log(`  ${'─'.repeat(56)}`)
    console.log(`  ${'tiptap'.padEnd(28)} components/custom/content-editor/tiptap-*/ (all files)`)
    console.log()

    clack.outro(`${all.length} components available`)
    return
  }

  const config = await resolveConfig(cwd)
  const admin = path.resolve(cwd, config.paths.admin)

  if (!fs.existsSync(admin)) {
    clack.cancel(
      `Admin directory not found at ${config.paths.admin}. Run 'betterstart init' first.`
    )
    process.exit(1)
  }

  if (options.shadcnPreset) {
    runShadcnPresetUpdate({
      cwd,
      config,
      preset: options.shadcnPreset.trim(),
      only: normalizedOnly
    })
    return
  }

  if (!options.all && components.length === 0) {
    clack.log.error(
      'Provide component names or use --all. Run with --list to see available components.'
    )
    process.exit(1)
  }

  clack.intro('BetterStart Update Components')

  const toUpdate = options.all ? getAllComponentNamesForConfig(config) : components
  const uiDir = resolveCliAssetPath('shared-assets', 'react-admin', 'ui')
  const customDir = resolveCliAssetPath('shared-assets', 'react-admin', 'custom')
  let updated = 0
  let skipped = 0
  const updatedTemplateNames = new Set<string>()
  const updatedStaticNames = new Set<string>()
  const requiredPackageDependencies = new Set<string>()

  function trackPackageDependencies(name: string): void {
    for (const dependency of COMPONENT_PACKAGE_DEPENDENCIES[name] ?? []) {
      requiredPackageDependencies.add(dependency)
    }
  }

  function writeTemplateEntry(name: string, entry: TemplateEntry): boolean {
    if (updatedTemplateNames.has(name)) {
      return false
    }

    if (entry.requiredPlugin && !hasPlugin(config, entry.requiredPlugin)) {
      clack.log.warning(`${name} requires the ${entry.requiredPlugin} plugin and was skipped.`)
      skipped++
      return false
    }
    if (entry.requiredIntegration && !hasIntegration(config, entry.requiredIntegration)) {
      clack.log.warning(
        `${name} requires the ${entry.requiredIntegration} integration and was skipped.`
      )
      skipped++
      return false
    }

    const { relPath, content } = applyNamespaceToTemplateEntry(entry, config, cwd)
    const baseDir = entry.base === 'cwd' ? cwd : admin
    const destPath = path.join(baseDir, relPath)
    if (entry.preserveExisting && fs.existsSync(destPath)) {
      clack.log.info(`Preserved ${relPath}`)
      updatedTemplateNames.add(name)
      skipped++
      return false
    }
    fsExtra.ensureDirSync(path.dirname(destPath))
    fs.writeFileSync(destPath, content, 'utf-8')
    clack.log.success(`Updated ${relPath}`)
    updatedTemplateNames.add(name)
    trackPackageDependencies(name)
    updated++
    return true
  }

  function writeTemplateEntryWithDependencies(name: string, entry: TemplateEntry): boolean {
    const wroteEntry = writeTemplateEntry(name, entry)
    if (!wroteEntry) {
      return false
    }

    for (const dependencyName of entry.dependencies ?? []) {
      writeNamedDependency(dependencyName)
    }

    return true
  }

  function writeStaticAssetEntry(assetDirectory: 'ui' | 'custom', name: string): boolean {
    const key = `${assetDirectory}:${name}`
    if (updatedStaticNames.has(key)) {
      return false
    }

    const assetDir = assetDirectory === 'ui' ? uiDir : customDir
    const assetFile = findStaticAssetFile(assetDir, name)
    if (!assetFile) {
      return false
    }

    const namespace = config.frameworkConfig.next.namespace
    const namespacedAssetFile = applyAdminNamespaceToPath(assetFile, namespace)
    const destPath = path.join(admin, 'components', assetDirectory, namespacedAssetFile)
    fsExtra.ensureDirSync(path.dirname(destPath))
    writeNamespacedFile(path.join(assetDir, assetFile), destPath, namespace)
    clack.log.success(`Updated components/${assetDirectory}/${namespacedAssetFile}`)

    if (assetDirectory === 'custom') {
      const assetSubdir = path.join(assetDir, name)
      if (fs.existsSync(assetSubdir) && fs.statSync(assetSubdir).isDirectory()) {
        const namespacedName = applyAdminNamespaceToPath(name, namespace)
        const destSubdir = path.join(admin, 'components', assetDirectory, namespacedName)
        fsExtra.emptyDirSync(destSubdir)
        copyNamespacedDirectory(assetSubdir, destSubdir, namespace)
        clack.log.success(`Updated components/${assetDirectory}/${namespacedName}/ (all files)`)
      }
    }

    updatedStaticNames.add(key)
    trackPackageDependencies(name)
    updated++

    if (assetDirectory === 'custom') {
      for (const dependencyName of STATIC_CUSTOM_DEPENDENCIES[name] ?? []) {
        writeNamedDependency(dependencyName)
      }
    }

    return true
  }

  function writeTiptapTemplates(): boolean {
    const key = 'custom:tiptap'
    if (updatedStaticNames.has(key)) {
      return true
    }

    const srcBaseDir = resolveCliAssetPath(
      'shared-assets',
      'react-admin',
      'custom',
      'content-editor'
    )
    const namespace = config.frameworkConfig.next.namespace
    const destBaseDir = path.join(admin, 'components', 'custom', 'content-editor')
    if (!fs.existsSync(srcBaseDir)) {
      return false
    }

    let copied = false
    for (const directory of TIPTAP_CONTENT_EDITOR_DIRECTORIES) {
      const srcDir = path.join(srcBaseDir, directory)
      if (!fs.existsSync(srcDir)) {
        continue
      }

      const destDir = path.join(destBaseDir, applyAdminNamespaceToPath(directory, namespace))
      fsExtra.emptyDirSync(destDir)
      copyNamespacedDirectory(srcDir, destDir, namespace)
      copied = true
    }

    if (!copied) {
      return false
    }

    clack.log.success('Updated components/custom/content-editor/tiptap-*/ (all files)')
    updatedStaticNames.add(key)
    trackPackageDependencies('tiptap')
    updated++
    removeLegacyDirectory(path.join(admin, 'components', 'custom', 'tiptap'))
    for (const dependencyName of TIPTAP_TEMPLATE_DEPENDENCIES) {
      writeNamedDependency(dependencyName)
    }
    return true
  }

  function writeNamedDependency(name: string): void {
    const entry = TEMPLATE_REGISTRY[name]
    if (entry) {
      writeTemplateEntryWithDependencies(name, entry)
      return
    }

    if (writeStaticAssetEntry('ui', name)) {
      return
    }

    if (writeStaticAssetEntry('custom', name)) {
      return
    }

    if (name === 'tiptap') {
      writeTiptapTemplates()
    }
  }

  for (const name of toUpdate) {
    // 1. Check template registry (template functions)
    if (TEMPLATE_REGISTRY[name]) {
      const entry = TEMPLATE_REGISTRY[name]
      writeTemplateEntryWithDependencies(name, entry)
      continue
    }

    // 2. Check static UI templates
    if (writeStaticAssetEntry('ui', name)) {
      continue
    }

    // 3. Check static custom templates
    if (writeStaticAssetEntry('custom', name)) {
      continue
    }

    // 4. Special: TipTap support directories inside content-editor
    if (name === 'tiptap') {
      if (!writeTiptapTemplates()) {
        clack.log.warning('tiptap templates not found')
        skipped++
      }
      continue
    }

    clack.log.warning(`Unknown component: ${name}`)
    skipped++
  }

  syncInstalledPluginManifests(cwd, config)
  syncInstalledIntegrationManifests(cwd, config)

  const projectPackageJson = readProjectPackageJson(cwd)
  const missingPackageDependencies = Array.from(requiredPackageDependencies).filter(
    (dependency) => !hasDeclaredPackage(projectPackageJson, dependency)
  )
  if (missingPackageDependencies.length > 0) {
    clack.log.warning(
      `Updated components require package dependencies that are not declared: ${missingPackageDependencies.join(', ')}. Run 'pnpm betterstart update-deps' before building.`
    )
  }

  clack.outro(
    `Updated ${updated} component${updated !== 1 ? 's' : ''}${skipped > 0 ? `, ${skipped} skipped` : ''}`
  )
}

function removeLegacyDirectory(dirPath: string): void {
  if (fs.existsSync(dirPath)) {
    fs.rmSync(dirPath, { recursive: true, force: true })
  }
}

function validateShadcnPresetOptions(components: string[], options: UpdateCommandOptions): void {
  if (options.only && !options.shadcnPreset) {
    clack.cancel('--only can only be used with --shadcn-preset.')
    process.exit(1)
  }

  if (!options.shadcnPreset) {
    return
  }

  if (options.list) {
    clack.cancel('--list cannot be combined with --shadcn-preset.')
    process.exit(1)
  }

  if (options.all) {
    clack.cancel('--all cannot be combined with --shadcn-preset.')
    process.exit(1)
  }

  if (components.length > 0) {
    clack.cancel('Component names cannot be combined with --shadcn-preset.')
    process.exit(1)
  }
}

function normalizeShadcnPresetOnly(value: string | undefined): string | undefined {
  if (!value) {
    return undefined
  }

  const parts = value
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)

  if (parts.length !== 1 || parts[0] !== 'theme') {
    clack.cancel(
      'Admin-only shadcn preset updates currently support --only theme. Omit --only to apply the full preset.'
    )
    process.exit(1)
  }

  return 'theme'
}

function runShadcnPresetUpdate({
  cwd,
  config,
  preset,
  only
}: {
  cwd: string
  config: ResolvedBetterstartConfig
  preset: string
  only?: string
}): void {
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const adminGlobalsPath = path.join(cwd, config.paths.admin, namespace.globalsFile)
  const componentsJsonPath = path.join(cwd, 'components.json')
  const shadcnBackupPath = `${componentsJsonPath}.bak`
  const restoreAfterApplyPaths = [
    componentsJsonPath,
    shadcnBackupPath,
    path.join(cwd, config.paths.admin, 'lib', 'utils.ts'),
    ...getHostProjectFilesToRestore(cwd)
  ]

  if (!preset) {
    clack.cancel('--shadcn-preset requires a preset code, preset name, or preset URL.')
    process.exit(1)
  }

  if (!fs.existsSync(adminGlobalsPath)) {
    clack.cancel(
      `Admin globals file not found at ${path.relative(cwd, adminGlobalsPath)}. Run 'betterstart update admin-globals' first.`
    )
    process.exit(1)
  }

  const shadcnBin = resolveLocalShadcnBin(cwd)
  const restoreSnapshots = restoreAfterApplyPaths.map((filePath) => ({
    filePath,
    snapshot: snapshotFile(filePath)
  }))

  clack.intro('BetterStart Shadcn Preset')
  clack.log.info(`Applying preset to ${path.join(config.paths.admin, 'components/ui')}`)

  let failed = false
  try {
    fs.writeFileSync(
      componentsJsonPath,
      `${JSON.stringify(createAdminShadcnComponentsJson(config), null, 2)}\n`,
      'utf-8'
    )

    const args = ['apply', '--preset', preset, '--cwd', cwd, '--yes']
    if (only) {
      args.push('--only', only)
    }

    execFileSync(shadcnBin, args, { cwd, stdio: 'inherit' })
  } catch {
    failed = true
  } finally {
    for (const { filePath, snapshot } of restoreSnapshots.reverse()) {
      restoreFile(filePath, snapshot)
    }
  }

  if (failed) {
    clack.cancel('shadcn preset application failed.')
    process.exit(1)
  }

  clack.outro(
    only
      ? `Applied shadcn preset parts (${only}) to the Admin.`
      : 'Applied shadcn preset to Admin UI components and styles.'
  )
}

function createAdminShadcnComponentsJson(
  config: ResolvedBetterstartConfig
): AdminShadcnComponentsJson {
  const adminPath = toPosixPath(config.paths.admin)
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)

  return {
    $schema: 'https://ui.shadcn.com/schema.json',
    style: 'new-york',
    rsc: true,
    tsx: true,
    tailwind: {
      config: '',
      css: `${adminPath}/${namespace.globalsFile}`,
      baseColor: 'neutral',
      cssVariables: true,
      prefix: ''
    },
    iconLibrary: 'lucide',
    aliases: {
      components: `${namespace.alias}/components`,
      ui: `${namespace.alias}/components/ui`,
      hooks: `${namespace.alias}/hooks`,
      lib: `${namespace.alias}/lib`,
      utils: `${namespace.alias}/utils/shared/cn`
    }
  }
}

function toPosixPath(value: string): string {
  return value.replace(/\\/g, '/')
}

function resolveLocalShadcnBin(cwd: string): string {
  const binName = process.platform === 'win32' ? 'shadcn.cmd' : 'shadcn'
  const shadcnBin = path.join(cwd, 'node_modules', '.bin', binName)

  if (!fs.existsSync(shadcnBin)) {
    clack.cancel(
      `shadcn is not installed in this project. Run 'betterstart update-deps' and try again.`
    )
    process.exit(1)
  }

  return shadcnBin
}

function getHostProjectFilesToRestore(cwd: string): string[] {
  const hostRelativePaths = [
    'app/layout.tsx',
    'app/layout.ts',
    'app/layout.jsx',
    'app/layout.js',
    'src/app/layout.tsx',
    'src/app/layout.ts',
    'src/app/layout.jsx',
    'src/app/layout.js',
    'app/globals.css',
    'src/app/globals.css'
  ]

  return hostRelativePaths.map((relativePath) => path.join(cwd, relativePath))
}

function snapshotFile(filePath: string): FileSnapshot {
  if (!fs.existsSync(filePath)) {
    return { existed: false }
  }

  return { existed: true, content: fs.readFileSync(filePath, 'utf-8') }
}

function restoreFile(filePath: string, snapshot: FileSnapshot): void {
  if (snapshot.existed) {
    fs.writeFileSync(filePath, snapshot.content ?? '', 'utf-8')
    return
  }

  if (fs.existsSync(filePath)) {
    fs.rmSync(filePath, { force: true })
  }
}
